/**
 * WebMCP tools (https://webmachinelearning.github.io/webmcp/): what an AI agent in the visitor's
 * browser can do on this site through `document.modelContext`. Registered once per page load, and
 * only where the browser has WebMCP; elsewhere this does nothing.
 *
 * Two read-mostly tools: find something on ORTEX, and open one of its pages. The data itself is
 * for the ORTEX MCP server (mcp.ortex.com, listed in /.well-known/ard.json), which signs the
 * agent in to an account; these tools act as the visitor already on the page.
 */
type ToolInput = Record<string, unknown>;

interface WebMcpTool {
    name: string;
    title: string;
    description: string;
    inputSchema: object;
    annotations?: { readOnlyHint?: boolean };
    execute: (input: ToolInput) => Promise<unknown>;
}

interface ModelContext {
    registerTool: (tool: WebMcpTool, options?: { signal?: AbortSignal }) => Promise<void> | void;
    // Early implementations unregister by name rather than by abort signal.
    unregisterTool?: (name: string) => void;
}

const modelContext = (): ModelContext | undefined => {
    if (typeof document === "undefined") return undefined;
    const context = (document as unknown as { modelContext?: ModelContext }).modelContext ?? (navigator as unknown as { modelContext?: ModelContext }).modelContext;
    return typeof context?.registerTool === "function" ? context : undefined;
};

/** A path on this site from a path or a same-origin URL, or null for anything else. */
export const sitePath = (value: unknown): string | null => {
    if (typeof value !== "string" || !value.trim()) return null;
    try {
        const url = new URL(value.trim(), window.location.origin);
        if (url.origin !== window.location.origin) return null;
        return url.pathname + url.search + url.hash;
    } catch {
        return null;
    }
};

/** Registers the site's tools; the returned function removes them. */
export const registerSiteTools = (navigate: (path: string) => void): (() => void) => {
    const context = modelContext();
    if (!context) return () => {};

    const tools: WebMcpTool[] = [
        {
            name: "search_ortex",
            title: "Search ORTEX",
            description: "Find stocks, ETFs, indexes, people and pages on ORTEX by name or ticker. Returns up to 10 matches, each with the URL of its ORTEX page (open one with open_ortex_page).",
            inputSchema: {
                type: "object",
                properties: { query: { type: "string", description: "A company name, ticker (for example TSLA), person or page to look for." } },
                required: ["query"],
            },
            annotations: { readOnlyHint: true },
            execute: async ({ query }) => {
                const { searchOmni } = await import("@/controllers/find-instruments");
                const results = (await searchOmni(String(query ?? ""))) ?? [];
                return results.slice(0, 10).map((r) => ({ title: r.title, detail: r.subtitle, kind: r.kind, url: window.location.origin + r.route }));
            },
        },
        {
            name: "open_ortex_page",
            title: "Open an ORTEX page",
            description: "Open a page of this site in the current tab: a URL from search_ortex, or a path such as /s/nasdaq/tsla, /short-interest or /index-rebalance.",
            inputSchema: {
                type: "object",
                properties: { url: { type: "string", description: "A path on this site starting with /, or a full URL on this site." } },
                required: ["url"],
            },
            execute: async ({ url }) => {
                const path = sitePath(url);
                if (!path) return { error: "Only pages on this site can be opened." };
                navigate(path);
                return { opened: window.location.origin + path };
            },
        },
    ];

    const controller = new AbortController();
    tools.forEach((tool) => {
        try {
            const done = context.registerTool(tool, { signal: controller.signal });
            if (done && typeof (done as Promise<void>).catch === "function") (done as Promise<void>).catch(() => {});
        } catch {
            // A tool already registered (a second call on the same document) or an implementation
            // that rejects the shape: the page works the same without it.
        }
    });
    return () => {
        controller.abort();
        // Implementations without signal support unregister by name; some throw for a name they no
        // longer hold, which must not break the effect cleanup that calls this.
        tools.forEach((tool) => {
            try {
                context.unregisterTool?.(tool.name);
            } catch {
                // already gone
            }
        });
    };
};
